import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "./Auth/AuthContext";
import { useCompany } from "./Company/CompanyContext";
import api from "../api";
import {
  FaFileInvoice, FaTruck, FaEuroSign, FaUsers,
  FaCalendarAlt, FaMoneyCheckAlt, FaUserShield, FaChartBar, FaBoxes, FaCog, FaChartLine, FaTruckLoading,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { isOrgAdmin, isSuperadmin } from "../utils/roles";
import SuperadminHome from "./Admin/SuperadminHome";

/* ── Module definitions ── */
const MODULES = [
  { path: "/dashboard",  perm: "dashboard",    icon: FaChartBar,      labelKey: "navbar.dashboard",    descKey: "home.featureDashboard",    grad: "from-orange-500 to-amber-400" },
  { path: "/missions",   perm: "missions",     icon: FaTruck,         labelKey: "navbar.missions",     descKey: "home.feature2Desc",        grad: "from-sky-500 to-cyan-400" },
  { path: "/quotes",     perm: "quote",        icon: FaFileInvoice,   labelKey: "navbar.quotes",       descKey: "home.feature1Desc",        grad: "from-indigo-500 to-violet-400" },
  { path: "/catalogue",  perm: "quote",        icon: FaBoxes,         labelKey: "navbar.catalogue",    descKey: "home.featureCatalogueDesc", grad: "from-violet-500 to-purple-400" },
  { path: "/invoices",   perm: "invoices",     icon: FaEuroSign,      labelKey: "navbar.invoices",     descKey: "home.feature3Desc",        grad: "from-emerald-500 to-teal-400" },
  { path: "/investors",  perm: "investors",    icon: FaUsers,         labelKey: "navbar.investors",    descKey: "home.featureFinanceDesc",  grad: "from-fuchsia-500 to-pink-400" },
  { path: "/salaries",   perm: "salaries",     icon: FaMoneyCheckAlt, labelKey: "navbar.salaries",     descKey: "home.featureSalaryDesc",   grad: "from-amber-500 to-yellow-400" },
  { path: "/subcontractors", perm: "subcontractors", icon: FaTruckLoading, labelKey: "navbar.subcontractors", descKey: "home.featureSubcontractorsDesc", grad: "from-rose-500 to-orange-400" },
  { path: "/calendar",   perm: "appointments", icon: FaCalendarAlt,   labelKey: "navbar.calendar",     descKey: "home.feature5Desc",        grad: "from-purple-500 to-indigo-400" },
];
const ADMIN_MODULES = [
  { path: "/performance", icon: FaChartLine,  labelKey: "navbar.performance", descKey: "home.featurePerformanceDesc", grad: "from-orange-500 to-rose-400" },
  { path: "/users",       icon: FaUserShield, labelKey: "navbar.manageUsers", descKey: "home.featureUsersDesc",       grad: "from-cyan-500 to-sky-400" },
  { path: "/settings",    icon: FaCog,        labelKey: "navbar.settings",    descKey: "home.featureSettingsDesc",    grad: "from-slate-500 to-slate-400" },
];

function ModuleCard({ mod, t }) {
  const Icon = mod.icon;
  return (
    <Link
      to={mod.path}
      className="group relative flex flex-col gap-3 p-5 rounded-2xl border border-base-200 bg-base-100 overflow-hidden transition-[transform,box-shadow,border-color] duration-150 ease-out hover:-translate-y-1 hover:shadow-lg hover:border-base-300 active:scale-[0.97] active:shadow-sm"
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${mod.grad} opacity-0 group-hover:opacity-[0.04] transition-opacity duration-150 rounded-2xl pointer-events-none`} />

      <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${mod.grad} flex items-center justify-center text-white shadow-sm shrink-0`}>
        <Icon className="text-lg" />
      </div>
      <div>
        <h3 className="font-semibold text-base-content text-sm tracking-tight group-hover:text-primary transition-colors duration-150">
          {t(mod.labelKey)}
        </h3>
        <p className="text-xs text-base-content/45 mt-0.5 leading-snug line-clamp-2">
          {t(mod.descKey)}
        </p>
      </div>

      <span className="absolute top-4 right-4 text-base-content/20 text-xs group-hover:text-primary/40 transition-colors duration-150">→</span>
    </Link>
  );
}

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const { user, impersonating } = useAuth();
  const { company, logoPath } = useCompany();

  const isAdmin = isOrgAdmin(user);
  const canSeeAppts = isAdmin || (user?.permissions || []).includes("appointments");
  const canSeeQuotes = isAdmin || (user?.permissions || []).includes("quote");

  // Résumé du jour : RDV aujourd'hui + devis en attente (best-effort, selon permissions).
  const [todayCount, setTodayCount] = useState(null);
  const [pendingCount, setPendingCount] = useState(null);

  useEffect(() => {
    let alive = true;
    const ymd = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD local
    if (canSeeAppts) {
      api.get("/appointments").then((r) => {
        if (!alive) return;
        const n = (r.data || []).filter((a) => String(a.date || "").slice(0, 10) === ymd).length;
        setTodayCount(n);
      }).catch(() => {});
    }
    if (canSeeQuotes) {
      api.get("/quote?status=pending&limit=1").then((r) => {
        if (!alive) return;
        setPendingCount(r.data?.pagination?.total ?? 0);
      }).catch(() => {});
    }
    return () => { alive = false; };
  }, [canSeeAppts, canSeeQuotes]);

  const greeting = new Date().getHours() >= 18 ? t("home.greetingEvening") : t("home.greetingDay");
  const todayLabel = new Date().toLocaleDateString(i18n.language === "fr" ? "fr-FR" : "en-US", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  const userPerms = isAdmin ? MODULES.map(m => m.perm) : (user?.permissions || []);
  const visibleModules = MODULES.filter(m => isAdmin || userPerms.includes(m.perm));
  const allModules = [...visibleModules, ...(isAdmin ? ADMIN_MODULES : [])];

  const companyName = company?.name || "The Movers";
  const logoSrc = logoPath ? `${api.defaults.baseURL}${logoPath}` : "/logo.jpg";

  // Superadmin hors impersonation : org système (pas de données métier) →
  // accueil dédié avec accès direct aux organisations.
  if (isSuperadmin(user) && !impersonating) {
    return <SuperadminHome />;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 flex flex-col gap-6 py-8 animate-fade-in">

      {/* ── Hero ── */}
      <div className="relative overflow-hidden rounded-3xl border border-base-200 bg-gradient-to-br from-primary/12 via-primary/5 to-transparent p-6 sm:p-8">
        {/* blobs décoratifs subtils */}
        <div className="pointer-events-none absolute -top-16 -right-10 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 left-1/3 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />

        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          {/* Left: accueil + identité + CTA */}
          <div className="flex flex-col gap-4 max-w-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl overflow-hidden border border-base-200 shadow-sm bg-base-100 shrink-0">
                <img src={logoSrc} alt={companyName} className="w-full h-full object-contain" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-base-content leading-tight">
                  {companyName}
                </h1>
                {company.slogan && (
                  <p className="text-primary font-bold text-xs tracking-widest uppercase">
                    {company.slogan}
                  </p>
                )}
              </div>
            </div>

            <div>
              <h2 className="text-lg sm:text-xl font-bold text-base-content">
                {greeting}, {user?.username} <span className="inline-block">👋</span>
              </h2>
              <p className="text-sm text-base-content/50 mt-0.5 capitalize">{todayLabel}</p>
            </div>

            <p className="text-sm text-base-content/45 leading-relaxed hidden sm:block">
              {t("home.platformFeatures")}
            </p>

            {/* CTA */}
            <div className="flex flex-wrap gap-2 pt-1">
              {canSeeQuotes && (
                <Link to="/quotes" className="btn btn-primary btn-sm gap-1.5 shadow-sm">
                  <FaFileInvoice className="text-xs" /> {t("home.ctaNewQuote", "Nouveau devis")}
                </Link>
              )}
              {canSeeAppts && (
                <Link to="/calendar" className="btn btn-ghost btn-sm gap-1.5 border border-base-300">
                  <FaCalendarAlt className="text-xs" /> {t("home.ctaPlanning", "Voir le planning")}
                </Link>
              )}
            </div>
          </div>

          {/* Right: KPI vivants */}
          {(canSeeAppts || canSeeQuotes) && (
            <div className="flex gap-3 shrink-0">
              {canSeeAppts && (
                <StatTile
                  to="/calendar"
                  icon={FaCalendarAlt}
                  value={todayCount == null ? "…" : todayCount}
                  label={t("home.statTodayAppts", "RDV aujourd'hui")}
                  grad="from-purple-500 to-indigo-400"
                />
              )}
              {canSeeQuotes && (
                <StatTile
                  to="/quotes"
                  icon={FaFileInvoice}
                  value={pendingCount == null ? "…" : pendingCount}
                  label={t("home.statPendingQuotes", "Devis en attente")}
                  grad="from-indigo-500 to-violet-400"
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Module grid ── */}
      {allModules.length > 0 ? (
        <div className="flex flex-col gap-3">
          <h3 className="text-xs font-bold tracking-widest text-base-content/40 uppercase px-1">
            {t("home.quickAccess", "Accès rapide")}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {allModules.map((mod) => (
              <ModuleCard key={mod.path} mod={mod} t={t} />
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
          <div className="w-14 h-14 rounded-2xl bg-base-200 flex items-center justify-center">
            <FaTruck className="text-2xl text-base-content/25" />
          </div>
          <p className="text-sm text-base-content/40 max-w-xs leading-relaxed">
            {t("user.noModules")}
          </p>
        </div>
      )}

      {/* ── Intégrations partenaires ── */}
      <div className="flex flex-col gap-3">
        <h3 className="text-xs font-bold tracking-widest text-base-content/40 uppercase px-1">
          {t("home.integrations", "Interfacé avec")}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <IntegrationCard
            statusLabel={t("home.connected", "Connecté")}
            logo="/logos/qonto.png"
            alt="Qonto"
            accent="bg-[#1D1D3B]"
            desc={t("home.integrationQonto", "Facturation électronique")}
            href="https://app.qonto.com"
          />
          <IntegrationCard
            statusLabel={t("home.connected", "Connecté")}
            logo="/logos/nextories.png"
            alt="Nextories"
            accent="bg-indigo-500"
            desc={t("home.integrationNextories", "Leads & devis automatiques")}
            href="https://app.nextories.com"
          />
          <IntegrationCard
            statusLabel={t("home.connected", "Connecté")}
            logo="/logos/dbep.webp"
            alt="Des Bras en Plus"
            accent="bg-orange-500"
            desc={t("home.integrationDbep", "Missions synchronisées")}
            href="https://partenaire.desbrasenplus.com"
          />
        </div>
      </div>
    </div>
  );
};

function IntegrationCard({ statusLabel, logo, alt, accent, desc, href }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={`Ouvrir ${alt}`}
      className="group relative flex items-center gap-3 p-4 rounded-2xl border border-base-200 bg-base-100 overflow-hidden transition-[transform,box-shadow] duration-150 hover:-translate-y-0.5 hover:shadow-md active:scale-[0.98]"
    >
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${accent}`} />
      {/* Pastille blanche : rend les logos lisibles en thème clair comme sombre. */}
      <div className="shrink-0 h-11 w-14 rounded-lg bg-white border border-base-200 flex items-center justify-center p-1.5">
        <img src={logo} alt={alt} className="max-h-full max-w-full object-contain" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold text-base-content truncate flex items-center gap-1">
          {alt}
          <FaExternalLinkAlt className="text-[9px] text-base-content/30 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <div className="text-xs text-base-content/45 mt-0.5 truncate">{desc}</div>
      </div>
      <span className="flex items-center gap-1 shrink-0 text-[11px] font-semibold text-emerald-600">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        {statusLabel}
      </span>
    </a>
  );
}

function StatTile({ to, icon: Icon, value, label, grad }) {
  return (
    <Link
      to={to}
      className="group relative flex flex-col justify-between gap-3 w-32 sm:w-36 p-4 rounded-2xl border border-base-200 bg-base-100 shadow-sm overflow-hidden transition-[transform,box-shadow] duration-150 hover:-translate-y-1 hover:shadow-md active:scale-[0.97]"
    >
      <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${grad} flex items-center justify-center text-white shadow-sm`}>
        <Icon className="text-sm" />
      </div>
      <div>
        <div className="text-3xl font-extrabold text-base-content leading-none">{value}</div>
        <div className="text-[11px] text-base-content/45 mt-1 leading-snug">{label}</div>
      </div>
    </Link>
  );
}

export default HomePage;
